import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Crown, Users, UserCheck, AlertTriangle, Search, Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import UserSelector from './UserSelector';

interface SuperAdmin {
  user_email: string;
  assigned_at: string;
  assigned_by: number;
}

const SuperAdminAssigner = () => {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    loadSuperAdmins();
  }, []);

  const loadSuperAdmins = async () => {
    setIsLoadingData(true);
    try {
      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'updated_at',
        IsAsc: false,
        Filters: [
        {
          name: 'setting_key',
          op: 'StringStartsWith',
          value: 'superadmin_'
        }]

      });

      if (response.error) {
        console.error('Error loading superadmins:', response.error);
        return;
      }

      const superAdminList: SuperAdmin[] = [];

      // Add hardcoded superadmins
      const hardcodedAdmins = [
      'admin@teachai.com',
      'admin@ezsite.ai',
      'support@ezsite.ai'];


      hardcodedAdmins.forEach((email) => {
        superAdminList.push({
          user_email: email,
          assigned_at: '2024-01-01T00:00:00Z',
          assigned_by: 0
        });
      });

      // Add dynamic superadmins from database
      if (response.data?.List) {
        for (const setting of response.data.List) {
          if (setting.setting_key.startsWith('superadmin_email_')) {
            try {
              const settingData = JSON.parse(setting.setting_value);
              superAdminList.push({
                user_email: settingData.email,
                assigned_at: setting.updated_at,
                assigned_by: setting.updated_by
              });
            } catch (error) {
              console.error('Error parsing superadmin setting:', error);
            }
          }
        }
      }

      setSuperAdmins(superAdminList);
    } catch (error) {
      console.error('Error loading superadmins:', error);
      toast({
        title: 'Error',
        description: 'Failed to load SuperAdmin list',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const assignSuperAdmin = async (email?: string) => {
    const emailToAssign = email || searchEmail.trim();

    if (!emailToAssign) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive'
      });
      return;
    }

    // Check if already a superadmin
    if (superAdmins.some((admin) => admin.user_email === emailToAssign)) {
      toast({
        title: 'Error',
        description: 'This user is already a SuperAdmin',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const superAdminData = {
        email: emailToAssign,
        assigned_by: currentUser?.ID || 0,
        assigned_at: new Date().toISOString()
      };

      const settingKey = `superadmin_email_${emailToAssign.replace('@', '_at_').replace(/\./g, '_dot_')}`;

      const response = await window.ezsite.apis.tableCreate('15215', {
        setting_key: settingKey,
        setting_value: JSON.stringify(superAdminData),
        setting_description: `SuperAdmin assignment for ${emailToAssign}`,
        updated_by: currentUser?.ID || 0,
        updated_at: new Date().toISOString()
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Success',
        description: `Successfully assigned ${emailToAssign} as SuperAdmin`
      });

      setSearchEmail('');
      setShowUserSelector(false);
      await loadSuperAdmins();

    } catch (error: any) {
      console.error('Error assigning superadmin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign SuperAdmin',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeSuperAdmin = async (email: string) => {
    // Check if it's a hardcoded admin
    const hardcodedAdmins = ['admin@teachai.com', 'admin@ezsite.ai', 'support@ezsite.ai'];
    if (hardcodedAdmins.includes(email)) {
      toast({
        title: 'Error',
        description: 'Cannot remove hardcoded system administrators',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const settingKey = `superadmin_email_${email.replace('@', '_at_').replace(/\./g, '_dot_')}`;

      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: settingKey
        }]

      });

      if (response.error || !response.data?.List?.length) {
        throw new Error('SuperAdmin setting not found');
      }

      const settingId = response.data.List[0].id;
      const deleteResponse = await window.ezsite.apis.tableDelete('15215', { ID: settingId });

      if (deleteResponse.error) {
        throw new Error(deleteResponse.error);
      }

      toast({
        title: 'Success',
        description: `Removed SuperAdmin access for ${email}`
      });

      await loadSuperAdmins();

    } catch (error: any) {
      console.error('Error removing superadmin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove SuperAdmin',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isHardcodedAdmin = (email: string) => {
    return ['admin@teachai.com', 'admin@ezsite.ai', 'support@ezsite.ai'].includes(email);
  };

  return (
    <div className="space-y-6" data-id="oly8jo08b" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="if8wgah22" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

        {/* Assign SuperAdmin Card */}
        <Card data-id="f34zkrh4x" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
          <CardHeader data-id="3d4hunfis" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            <CardTitle className="flex items-center gap-2" data-id="04u1rib8q" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <Crown className="w-5 h-5 text-yellow-600" data-id="1k9p9tb31" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
              Assign SuperAdmin
            </CardTitle>
            <CardDescription data-id="3irr4arla" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              Grant SuperAdmin privileges to a user by email address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-id="wigral99o" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            <div className="space-y-2" data-id="ln2l2m0az" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <Label htmlFor="email" data-id="9pa77au7b" data-path="src/components/superadmin/SuperAdminAssigner.tsx">User Email</Label>
              <div className="flex gap-2" data-id="95vyj0pnz" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="flex-1" data-id="2b57jvcpv" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />

                <Button
                  onClick={() => assignSuperAdmin()}
                  disabled={isLoading || !searchEmail.trim()}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="7u3pfs4ic" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

                  {isLoading ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2" data-id="9w4y93bn9" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <Separator className="flex-1" data-id="qoqk3hust" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
              <span className="text-sm text-gray-500" data-id="br0tvjfnv" data-path="src/components/superadmin/SuperAdminAssigner.tsx">OR</span>
              <Separator className="flex-1" data-id="fizc6gito" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
            </div>

            <Button
              variant="outline"
              onClick={() => setShowUserSelector(!showUserSelector)}
              className="w-full" data-id="py2b1vnq5" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

              <Search className="w-4 h-4 mr-2" data-id="f010pzicf" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
              {showUserSelector ? 'Hide User List' : 'Select from User List'}
            </Button>
            
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="yu0a1qcu2" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="6wg8avhid" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
              <div className="text-sm text-amber-800" data-id="q828uz0kx" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <p className="font-medium" data-id="rcwhpu86f" data-path="src/components/superadmin/SuperAdminAssigner.tsx">Important:</p>
                <p data-id="0r5cdquoi" data-path="src/components/superadmin/SuperAdminAssigner.tsx">Users must have registered accounts to become SuperAdmins. The email must match their registered email exactly.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current SuperAdmins */}
        <Card data-id="e6jn084ly" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
          <CardHeader data-id="hbcslhub1" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            <CardTitle className="flex items-center gap-2" data-id="rjybxslgq" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <UserCheck className="w-5 h-5 text-green-600" data-id="wvxoutbze" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
              Current SuperAdmins
            </CardTitle>
            <CardDescription data-id="pzcwcrj4f" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              Users with SuperAdmin privileges ({superAdmins.length} total)
            </CardDescription>
          </CardHeader>
          <CardContent data-id="m7p7pdhnm" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            {isLoadingData ?
            <div className="flex items-center justify-center py-8" data-id="edxsjdrue" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-id="xtcg1d9k3" data-path="src/components/superadmin/SuperAdminAssigner.tsx"></div>
              </div> :
            superAdmins.length === 0 ?
            <div className="text-center py-8 text-gray-500" data-id="pxqbhwnbp" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <Crown className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="1ff97w7so" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
                <p data-id="93t8rfog1" data-path="src/components/superadmin/SuperAdminAssigner.tsx">No SuperAdmins assigned yet</p>
              </div> :

            <div className="space-y-3 max-h-80 overflow-y-auto" data-id="j2op5u28m" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                {superAdmins.map((admin, index) =>
              <motion.div
                key={`${admin.user_email}-${index}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="x27d74522" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

                    <div className="flex items-center gap-3" data-id="q4tyeof3y" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                      <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center" data-id="90quwlaoo" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                        <Crown className="w-4 h-4 text-white" data-id="qui7mhj00" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
                      </div>
                      <div data-id="2c2ww1109" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                        <p className="font-medium text-gray-900" data-id="0j91h4gs9" data-path="src/components/superadmin/SuperAdminAssigner.tsx">{admin.user_email}</p>
                        <div className="flex items-center gap-2" data-id="cpvg56jxt" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                          <p className="text-xs text-gray-500" data-id="xkykija6k" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                            Assigned {new Date(admin.assigned_at).toLocaleDateString()}
                          </p>
                          {isHardcodedAdmin(admin.user_email) &&
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-id="tjljz0lpg" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                              System
                            </Badge>
                      }
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="dondsjksx" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200" data-id="wp1rlioqu" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                        SuperAdmin
                      </Badge>
                      {admin.user_email !== currentUser?.Email && !isHardcodedAdmin(admin.user_email) &&
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSuperAdmin(admin.user_email)}
                    disabled={isLoading}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="a9vzmkxet" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

                          Remove
                        </Button>
                  }
                    </div>
                  </motion.div>
              )}
              </div>
            }
          </CardContent>
        </Card>
      </motion.div>

      {/* User Selector */}
      {showUserSelector &&
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="overflow-hidden" data-id="3hqnccagg" data-path="src/components/superadmin/SuperAdminAssigner.tsx">

          <UserSelector onSelectUser={(email) => {
          setSearchEmail(email);
          assignSuperAdmin(email);
        }} data-id="sdtoy6301" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
        </motion.div>
      }

      {/* Privileges Information */}
      <Card data-id="wa107kir1" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
        <CardHeader data-id="pca3fb5gf" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
          <CardTitle className="flex items-center gap-2" data-id="1o6jn1jer" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            <Users className="w-5 h-5 text-blue-600" data-id="tcb2is5ty" data-path="src/components/superadmin/SuperAdminAssigner.tsx" />
            SuperAdmin Privileges
          </CardTitle>
          <CardDescription data-id="gm8w7kfoy" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            What SuperAdmins can do in the system
          </CardDescription>
        </CardHeader>
        <CardContent data-id="tafh6l3i9" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="g7rl2o8nv" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
            <div className="space-y-2" data-id="401a397wb" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <h4 className="font-medium text-gray-900" data-id="2t4z4ts1r" data-path="src/components/superadmin/SuperAdminAssigner.tsx">System Management</h4>
              <ul className="text-sm text-gray-600 space-y-1" data-id="9q4wgn3ks" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <li data-id="jkhoy5ei4" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Manage API keys and configurations</li>
                <li data-id="2xv6492ou" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Access system statistics and health</li>
                <li data-id="08rbiywuf" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Configure global app settings</li>
                <li data-id="2ai9enql8" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Assign and manage user tiers</li>
              </ul>
            </div>
            <div className="space-y-2" data-id="8o62c8vqf" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
              <h4 className="font-medium text-gray-900" data-id="0z4ylfvo9" data-path="src/components/superadmin/SuperAdminAssigner.tsx">User Management</h4>
              <ul className="text-sm text-gray-600 space-y-1" data-id="nw4nl8r59" data-path="src/components/superadmin/SuperAdminAssigner.tsx">
                <li data-id="a5id99ewr" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Assign other SuperAdmins</li>
                <li data-id="0upg2dph7" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Manage user access levels</li>
                <li data-id="xfv8nlx92" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• View all system activity</li>
                <li data-id="617b1jcbm" data-path="src/components/superadmin/SuperAdminAssigner.tsx">• Override tier restrictions</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default SuperAdminAssigner;